import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './LazyBackgroundVideo.module.scss';

interface Props {
  videoSrc: string;
  poster?: string;
  threshold?: number;
  opacity?: number;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  sx?: CSSProperties;
}

const LazyBackgroundVideo: React.FC<Props> = ({
  videoSrc,
  poster,
  threshold = 0.2,
  opacity = 0.4,
  autoPlay = true,
  muted = true,
  loop = true,
  sx,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const parent = videoRef.current?.parentElement;
    if (!parent) return;

    const computed = window.getComputedStyle(parent);
    if (computed.position === 'static') {
      parent.style.position = 'relative';
    }
    parent.style.overflow = parent.style.overflow || 'hidden';

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(parent);
    return () => io.disconnect();
  }, [threshold]);

  return (
    <video
      ref={videoRef}
      className={classNames(styles.lbv_bg, ready ? styles.lbv_ready : '')}
      playsInline
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      preload="none"
      poster={poster}
      src={shouldLoad ? videoSrc : undefined}
      onLoadedData={() => setReady(true)}
      style={{
        ...sx,
        opacity: ready ? opacity : 0,
      }}
    />
  );
};

export default LazyBackgroundVideo;
